import type { Maybe } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MatchAPI } from '@adornis/bh-matching-matches/api/matchAPI.js';
import { BuddyPageManageMatches } from '@adornis/bh-matching-matches/components/buddy-page-manage-matches.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { virtualize } from '@adornis/chemistry/directives/when-seen.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';

@customElement('mentoring-page-manage-matches')
export class MentoringPageManageMatches extends BuddyPageManageMatches {
  override _matchItem(match: Match, index: number, onLoad: (index: number) => void) {
    const mentor = match.mentor as Maybe<BuddyUserMentor>;
    const mentee = match.mentee as Maybe<BuddyUserMentee>;

    const isOpen = this._isOpen.has(match._id);

    return virtualize(
      html`
        <x-card ${css({ width: '100%', position: 'relative' })} @click=${() => this._isOpen.add(match._id)} space="md">
          ${isOpen
            ? html`
                <x-flex horizontal space="md" ${css({ paddingTop: this.spacing.md })}>
                  ${!mentee
                    ? html` <x-flex center flex> <x-text tone="warning"> - Mentee wurde gelöscht - </x-text> </x-flex> `
                    : html`
                        <x-flex space="md" flex>
                          <x-flex space="xs">
                            <x-flex horizontal start>
                              <x-button mode="text" icon="mail" @click=${() => this._sendReminderMail(match, mentee)}>
                                ${this._translator.translate('buddy', 'matchActions', 'sendReminderMail')}
                              </x-button>
                            </x-flex>
                            <x-flex horizontal start>
                              <x-button
                                mode="text"
                                icon="mail"
                                @click=${async () => this._sendFreeFormMails([match], BuddyUserMentee)}
                              >
                                ${this._translator.translate('buddy', 'matchActions', 'sendFreeformMail')}
                              </x-button>
                            </x-flex>
                          </x-flex>
                          <mentoring-user-details
                            flex
                            always-open
                            .user=${mentee}
                            .fieldBlackList=${this.menteeFieldBlackList}
                          ></mentoring-user-details>
                        </x-flex>
                      `}
                  ${!mentor
                    ? html` <x-flex center flex> <x-text tone="warning"> - Mentor wurde gelöscht - </x-text> </x-flex> `
                    : html`
                        <x-flex space="md" flex>
                          <x-flex space="xs">
                            <x-flex horizontal end>
                              <x-button mode="text" icon="mail" @click=${() => this._sendReminderMail(match, mentor)}>
                                ${this._translator.translate('buddy', 'matchActions', 'sendReminderMail')}
                              </x-button>
                            </x-flex>
                            <x-flex horizontal end>
                              <x-button
                                mode="text"
                                icon="mail"
                                @click=${async () => this._sendFreeFormMails([match], BuddyUserMentor)}
                              >
                                ${this._translator.translate('buddy', 'matchActions', 'sendFreeformMail')}
                              </x-button>
                            </x-flex>
                          </x-flex>
                          <mentoring-user-details
                            flex
                            always-open
                            .user=${mentor}
                            .fieldBlackList=${this.mentorFieldBlackList}
                          ></mentoring-user-details>
                        </x-flex>
                      `}
                </x-flex>
                <x-flex horizontal end>
                  <x-button
                    mode="text"
                    icon="delete"
                    tone="error"
                    @click=${() =>
                      TrDialog.guard(() => match.remove(), {
                        hasToConfirm: true,
                        confirmText: this._translator.translate('buddy', 'removeMatchConfirm'),
                        success: () => XSnackbar.show(this._translator.translate('buddy', 'removeMatchSuccessful')),
                      })}
                  >
                    ${this._translator.translate('buddy', 'removeMatch')}
                  </x-button>
                </x-flex>
                <x-hr></x-hr>
                ${this._userComments(match)}
              `
            : html`
                <x-flex horizontal space="md" ${css({ paddingTop: this.spacing.md })}>
                  ${!mentee
                    ? html` <x-flex center flex> <x-text tone="warning"> - Mentee wurde gelöscht - </x-text> </x-flex> `
                    : html` <buddy-user-details flex .user=${mentee} hide-toggle></buddy-user-details> `}
                  ${!mentor
                    ? html` <x-flex center flex> <x-text tone="warning"> - Mentor wurde gelöscht - </x-text> </x-flex> `
                    : html` <buddy-user-details flex hide-toggle .user=${mentor}></buddy-user-details> `}
                </x-flex>
              `}
          <x-button
            mode=${isOpen ? 'icon' : 'text'}
            icon=${isOpen ? 'expand_more' : 'chevron_right'}
            ${css({ position: 'absolute', top: this.spacing.sm, right: this.spacing.sm })}
            @click=${(e: MouseEvent) => {
              e.stopPropagation();
              if (isOpen) this._isOpen.delete(match._id);
              else this._isOpen.add(match._id);
            }}
          >
            ${isOpen ? '' : this._translator.translate('buddy', 'showAll')}
          </x-button>
        </x-card>
      `,
      oldHeight => html` <div ${css({ height: `${oldHeight}px`, gridColumn: '1 / -1' })}></div> `,
      {
        wrapperDisplayContents: true,
        onRender: () => {
          onLoad(index);
        },
      },
    );
  }

  override _bulkMail() {
    return html`
      <x-flex horizontal space-between>
        <x-button
          icon="mail"
          mode="outline"
          @click=${async () => {
            const timeFrame = this.currentTimeframe.value;
            if (!timeFrame) return;

            const matches = await MatchAPI.getFiltered(timeFrame._id, this._filter)(selectionSet(() => Match, 2));

            //const mailAddresses = MatchAPI.getAllEmailAddressFromMatches(matches);
            this._sendFreeFormMails(matches, BuddyUserMentee);
          }}
        >
          ${this._translator.translate('buddy', 'sendCircularMail', 'mentees')}
        </x-button>
        <x-button
          icon="mail"
          mode="outline"
          @click=${async () => {
            const timeFrame = this.currentTimeframe.value;
            if (!timeFrame) return;

            const matches = await MatchAPI.getFiltered(timeFrame._id, this._filter)(selectionSet(() => Match, 2));
            this._sendFreeFormMails(matches);
          }}
        >
          ${this._translator.translate('buddy', 'sendCircularMail', 'all')}
        </x-button>

        <x-button
          icon="mail"
          mode="outline"
          @click=${async () => {
            const timeFrame = this.currentTimeframe.value;
            if (!timeFrame) return;

            const matches = await MatchAPI.getFiltered(timeFrame._id, this._filter)(selectionSet(() => Match, 2));
            this._sendFreeFormMails(matches, BuddyUserMentor);
          }}
        >
          ${this._translator.translate('buddy', 'sendCircularMail', 'mentors')}
        </x-button>
      </x-flex>
    `;
  }
}
